/**
 * Pure helpers for the Test-due roster page.
 *
 * Kept in a separate module so they're easy to test without rendering
 * React. The page itself just imports + uses these.
 */

// ─── Types (mirror the API response from /scheduling/test-due-roster) ──

export type DueBucket = "overdue" | "due_now" | "due_soon" | "upcoming";

export interface RosterSite {
    site_id: string;
    site_name: string | null;
    address_line: string | null;
    city: string | null;
    region: string | null;
    postal_code: string | null;
    customer_id: string;
    customer_name: string;
    water_provider_id: string | null;
    water_provider_name: string | null;
    asset_count: number;
    overdue_count: number;
    earliest_due_date: string | null;
    latest_due_date: string | null;
    asset_types: string[];
    queued_visit_job_id: string | null;
    due_bucket: DueBucket;
}

export interface RosterFilters {
    daysAhead: number;
    includeOverdue: boolean;
    hideAlreadyQueued: boolean;
    customerId: string | null;
    waterProviderId: string | null;
    assetType: string | null;
}

// ─── Bucket presentation ────────────────────────────────────────────────

export interface BucketStyle {
    label: string;
    /** Tailwind class string for a Badge. */
    badge: string;
}

/**
 * Map a `due_bucket` value to a display label + badge color class.
 * Keep these in sync with the project's design tokens (success/warning/destructive).
 */
export function bucketStyle(bucket: DueBucket): BucketStyle {
    switch (bucket) {
        case "overdue":
            return { label: "Overdue", badge: "bg-destructive/10 text-destructive" };
        case "due_now":
            return { label: "Due now", badge: "bg-warning/10 text-warning" };
        case "due_soon":
            return { label: "Due soon", badge: "bg-amber-500/10 text-amber-600" };
        case "upcoming":
        default:
            return { label: "Upcoming", badge: "bg-muted text-foreground/80" };
    }
}

// ─── Address formatting ─────────────────────────────────────────────────

/**
 * One-line address string for compact roster display. Skips empty
 * pieces so a site missing city/region doesn't render a sad dangling
 * comma. Returns null when there's nothing to show.
 */
export function formatAddressOneLine(site: Pick<RosterSite, "address_line" | "city" | "region" | "postal_code">): string | null {
    const parts: string[] = [];
    if (site.address_line && site.address_line.trim()) parts.push(site.address_line.trim());
    const cityRegion = [site.city, site.region].filter((p) => !!p && p.trim()).join(", ");
    if (cityRegion) parts.push(cityRegion);
    if (site.postal_code && site.postal_code.trim()) parts.push(site.postal_code.trim());
    return parts.length ? parts.join(" • ") : null;
}

// ─── Asset-type chip text ───────────────────────────────────────────────

const ASSET_TYPE_LABELS: Record<string, string> = {
    backflow: "Backflow",
    extinguisher: "Extinguisher",
    e_light: "Emergency light",
    hood: "Hood",
    hydrant: "Hydrant",
};

export function labelForAssetType(t: string): string {
    return ASSET_TYPE_LABELS[t] ?? t;
}

// ─── Query-param builders ───────────────────────────────────────────────

/**
 * Build the URLSearchParams for a roster fetch from a (possibly
 * partial) filter object. Omits empty / default values to keep URLs
 * tidy when sharing links.
 */
export function buildRosterParams(
    filters: Partial<RosterFilters> & { limit?: number; offset?: number },
): URLSearchParams {
    const p = new URLSearchParams();
    if (typeof filters.daysAhead === "number") p.set("daysAhead", String(filters.daysAhead));
    if (filters.includeOverdue === false) p.set("includeOverdue", "false");
    if (filters.hideAlreadyQueued === false) p.set("hideAlreadyQueued", "false");
    if (filters.customerId) p.set("customerId", filters.customerId);
    if (filters.waterProviderId) p.set("waterProviderId", filters.waterProviderId);
    if (filters.assetType) p.set("assetType", filters.assetType);
    if (typeof filters.limit === "number") p.set("limit", String(filters.limit));
    if (typeof filters.offset === "number") p.set("offset", String(filters.offset));
    return p;
}

// ─── Days-until friendly text ───────────────────────────────────────────

/**
 * Render a friendly "due in N days" / "N days overdue" / "due today"
 * label for the roster's earliest_due_date column.
 *
 * `today` is injected for testability — pass `new Date()` from the
 * caller, which lets tests pin a fixed date and avoid midnight flake.
 */
export function daysUntilLabel(earliestDueIso: string | null, today: Date): string {
    if (!earliestDueIso) return "—";
    const due = parseIsoDate(earliestDueIso);
    if (!due) return "—";
    const days = diffDays(stripTime(today), stripTime(due));
    if (days === 0) return "Due today";
    if (days < 0) {
        const overdue = Math.abs(days);
        // Safety net for any straggler with a bad imported date that
        // slips past the server's 3-year overdue cap — don't print
        // "4,204 days overdue".
        if (overdue > 1095) return "Over 3 years overdue";
        return `${overdue} day${overdue === 1 ? "" : "s"} overdue`;
    }
    return `Due in ${days} day${days === 1 ? "" : "s"}`;
}

function parseIsoDate(s: string): Date | null {
    // Expect YYYY-MM-DD; tolerate full ISO too.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
}

function stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function diffDays(from: Date, to: Date): number {
    const ms = to.getTime() - from.getTime();
    return Math.round(ms / (24 * 60 * 60 * 1000));
}
